import { C, themeCSS, glassStyle, getInputStyle } from "./theme"
import { useState } from 'react'
import { useAuth } from './AuthContext'
import { errorMessageFor } from './utils/errorMessages'


function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  )
}

export default function AuthPage({ onBack, onNavigate, accountType, inviteCode }) {
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth()
  const [mode, setMode] = useState('login') // login | signup | forgot
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const handleSubmit = async () => {
    setLoading(true); setError(null); setMessage(null)

    if (mode === 'forgot') {
      if (!email) { setError('Please enter your email address.'); setLoading(false); return }
      const { error } = await resetPassword(email)
      if (error) { setError(errorMessageFor(error)); setLoading(false); return }
      setMessage('Password reset email sent! Check your inbox.')
      setLoading(false)
      return
    }

    if (!email || !password) { setError('Please enter your email and password.'); setLoading(false); return }
    if (mode === 'signup') {
      const { error } = await signUp(email, password)
      if (error) { setError(errorMessageFor(error)); setLoading(false); return }
      // Auto-login after successful signup
      const { error: loginError } = await signIn(email, password)
      if (loginError) {
        // Signup succeeded but auto-login failed — fall back to message
        setMessage('Account created! Please log in.')
      }
      // If signIn succeeds, AuthContext picks up the session automatically
    } else {
      const { error } = await signIn(email, password)
      // classifyError already maps "invalid credentials" to a friendly "Wrong
      // email or password" — but we pass the network/server/rate-limit cases
      // through too so users learn to retry instead of retyping forever.
      if (error) setError(errorMessageFor(error))
    }
    setLoading(false)
  }

  const handleGoogle = async () => {
    setGoogleLoading(true); setError(null)
    const { error } = await signInWithGoogle()
    if (error) { setError(errorMessageFor(error)); setGoogleLoading(false) }
  }

  const switchMode = (newMode) => {
    setMode(newMode)
    setError(null)
    setMessage(null)
  }

  const inputStyle = {
    ...getInputStyle(),
    marginBottom: 14,
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `radial-gradient(1200px 800px at 10% 0%, ${C.accent}14 0%, transparent 60%), radial-gradient(1000px 700px at 90% 10%, ${C.gold}18 0%, transparent 60%), radial-gradient(900px 600px at 50% 100%, ${C.purple}10 0%, transparent 60%), linear-gradient(180deg, ${C.bg} 0%, ${C.bg} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px', fontFamily: "'Outfit', sans-serif", color: C.text, position: 'relative',
    }}>

      {onBack && (
        <button onClick={onBack} style={{ position: 'absolute', top: 20, left: 16, display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', padding: '8px 0', minHeight: 44 }}>
          ← Back
        </button>
      )}

      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ width: 72, height: 72, borderRadius: 22, background: `linear-gradient(135deg, ${C.accent}22 0%, ${C.accent}10 100%)`, border: `1px solid ${C.accent}33`, backdropFilter: 'blur(18px) saturate(160%)', WebkitBackdropFilter: 'blur(18px) saturate(160%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(15,110,86,0.18)' }}><svg width="40" height="40" viewBox="0 0 64 64" fill="none"><rect x="4" y="4" width="56" height="56" rx="15" fill="#0F6E56"/><circle cx="32" cy="13" r="5.5" fill="#BA7517"/><circle cx="32" cy="13" r="2.2" fill="#0F6E56"/><path d="M32 18.5C32 18.5 38 25.5 38 31c0 3.8-2.2 6-6 7.8s-7.5 4-7.5 7.7c0 2.2 1.3 3.5 4.5 3.5" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none"/><circle cx="31" cy="50.5" r="3.5" fill="#BA7517"/><circle cx="31" cy="50.5" r="1.4" fill="#0F6E56"/></svg></div>
          </div>
          <div style={{ fontSize: 'clamp(36px, 8vw, 50px)', fontWeight: 400, letterSpacing: -1.5, color: C.accent, marginBottom: 8, fontFamily: C.serif }}>Settle</div>
          <p style={{ color: C.muted, fontSize: 15, margin: 0 }}>
            {mode === 'forgot' ? 'Reset your password.'
              : accountType === 'hr' ? (mode === 'login' ? 'Welcome back to your HR dashboard.' : 'Set up your team\'s relocation hub.')
              : inviteCode ? 'Your employer invited you to Settle.'
              : mode === 'login' ? 'Welcome back. Your plan awaits.'
              : 'Start your Gulf relocation journey.'}
          </p>
          {accountType === 'hr' && mode !== 'forgot' && (
            <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 99, background: `linear-gradient(135deg, ${C.purple}22 0%, ${C.purple}10 100%)`, border: `1px solid ${C.purple}33`, backdropFilter: 'blur(14px) saturate(160%)', WebkitBackdropFilter: 'blur(14px) saturate(160%)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, letterSpacing: 1.3, textTransform: 'uppercase' }}>HR Dashboard</span>
            </div>
          )}
          {inviteCode && mode !== 'forgot' && (
            <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 99, background: `linear-gradient(135deg, ${C.accent}22 0%, ${C.accent}10 100%)`, border: `1px solid ${C.accent}33`, backdropFilter: 'blur(14px) saturate(160%)', WebkitBackdropFilter: 'blur(14px) saturate(160%)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: 1.3, textTransform: 'uppercase' }}>Invite: {inviteCode}</span>
            </div>
          )}
        </div>

        <div style={{ ...glassStyle({ strong: true }), borderRadius: 24, padding: '30px 26px' }}>

          {/* Google sign-in — primary (hidden on forgot password) */}
          {mode !== 'forgot' && (
            <>
              <button onClick={handleGoogle} disabled={googleLoading} style={{
                width: '100%', padding: '15px 20px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 12, borderRadius: 14, cursor: googleLoading ? 'not-allowed' : 'pointer',
                fontSize: 15, fontFamily: 'inherit', fontWeight: 700, transition: 'all 0.15s',
                minHeight: 54, opacity: googleLoading ? 0.6 : 1,
                ...glassStyle(), color: C.text,
                marginBottom: 22,
              }}
                onMouseOver={e => { if (!googleLoading) e.currentTarget.style.borderColor = '#4285F4' }}
                onMouseOut={e => e.currentTarget.style.borderColor = C.glassBorder}
              >
                <GoogleIcon />
                {googleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
                <div style={{ flex: 1, height: 1, background: C.glassBorder }} />
                <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase' }}>or use email</span>
                <div style={{ flex: 1, height: 1, background: C.glassBorder }} />
              </div>
            </>
          )}

          {/* Email field */}
          <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase' }}>Email</label>
          <input style={inputStyle} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" />

          {/* Password field (hidden on forgot password) */}
          {mode !== 'forgot' && (
            <>
              <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase' }}>Password</label>
              <input style={inputStyle} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            </>
          )}

          {/* Forgot password link (login mode only) */}
          {mode === 'login' && (
            <div style={{ textAlign: 'right', marginBottom: 14, marginTop: -8 }}>
              <span onClick={() => switchMode('forgot')} style={{ color: C.accent, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Forgot password?
              </span>
            </div>
          )}

          {error && (
            <div style={{
              color: C.danger, fontSize: 14, marginBottom: 14, padding: '12px 14px',
              background: `linear-gradient(135deg, ${C.danger}22 0%, ${C.danger}10 100%)`,
              border: `1px solid ${C.danger}33`,
              backdropFilter: 'blur(14px) saturate(160%)', WebkitBackdropFilter: 'blur(14px) saturate(160%)',
              borderRadius: 12,
            }}>{error}</div>
          )}
          {message && (
            <div style={{
              color: C.accent, fontSize: 14, marginBottom: 14, padding: '12px 14px',
              background: `linear-gradient(135deg, ${C.accent}22 0%, ${C.accent}10 100%)`,
              border: `1px solid ${C.accent}33`,
              backdropFilter: 'blur(14px) saturate(160%)', WebkitBackdropFilter: 'blur(14px) saturate(160%)',
              borderRadius: 12,
            }}>{message}</div>
          )}

          <button onClick={handleSubmit} disabled={loading} style={{
            width: '100%', padding: '17px', background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentMid} 100%)`, color: '#fff',
            fontWeight: 700, fontSize: 16, border: 'none', borderRadius: 14,
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
            fontFamily: 'inherit', marginBottom: 20, minHeight: 56,
            boxShadow: '0 12px 32px rgba(15,110,86,0.32)',
          }}>
            {loading ? 'Please wait...' : mode === 'forgot' ? 'Send Reset Link →' : mode === 'login' ? 'Log In →' : 'Create Account →'}
          </button>

          <div style={{ textAlign: 'center', color: C.muted, fontSize: 14 }}>
            {mode === 'forgot' ? (
              <>
                Remember your password?{' '}
                <span onClick={() => switchMode('login')} style={{ color: C.accent, cursor: 'pointer', fontWeight: 700 }}>
                  Log in
                </span>
              </>
            ) : mode === 'login' ? (
              <>
                Don't have an account?{' '}
                <span onClick={() => switchMode('signup')} style={{ color: C.accent, cursor: 'pointer', fontWeight: 700 }}>
                  Sign up
                </span>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <span onClick={() => switchMode('login')} style={{ color: C.accent, cursor: 'pointer', fontWeight: 700 }}>
                  Log in
                </span>
              </>
            )}
          </div>
        </div>

        {/* Legal links */}
        <div style={{ textAlign: 'center', marginTop: 22, display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span onClick={() => onNavigate && onNavigate('privacy')} style={{ color: C.subtle, fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>Privacy Policy</span>
          <span style={{ color: C.subtle, fontSize: 10 }}>·</span>
          <span onClick={() => onNavigate && onNavigate('terms')} style={{ color: C.subtle, fontSize: 10, cursor: 'pointer', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>Terms of Service</span>
        </div>

        <p style={{ textAlign: 'center', color: C.muted, fontSize: 12, marginTop: 12 }}>Your AI-powered Gulf relocation companion</p>
      </div>
    </div>
  )
}
